import { db, ref, onValue, get, set, update } from '@/firebase'

class DataManager {
  constructor() {
    this.data = {
      siteStats: {
        pageViews: 0,
        uniqueVisitors: 0,
        averageTime: '--:--',
        pageCount: 0,
        startDate: '2026-01-31'
        // 注意：todayViews 不再存储在 siteStats 中，只使用 todayStats
      },
      recentVisits: [],
      pageStats: {},
      trendData: [],
      durationStats: {
        totalSeconds: 0,
        visits: 0
      },
      knownVisitors: [],
      todayStats: {
        date: new Date().toISOString().split('T')[0],
        views: 0
      }
    }
    this.listeners = []
    this.isInitialized = false
    this.isInitializing = false
    this.cacheExpiry = 5 * 60 * 1000 // 缓存过期时间：5分钟
  }

  static getInstance() {
    if (!DataManager.instance) {
      DataManager.instance = new DataManager()
    }
    return DataManager.instance
  }

  // 从本地存储加载缓存
  loadFromCache() {
    try {
      const cachedData = localStorage.getItem('ournote_data_cache')
      const cacheMeta = localStorage.getItem('ournote_data_cache_meta')
      
      if (cachedData && cacheMeta) {
        const parsedMeta = JSON.parse(cacheMeta)
        const now = Date.now()
        
        // 检查缓存是否过期
        if (now - parsedMeta.timestamp < this.cacheExpiry) {
          const parsedData = JSON.parse(cachedData)
          this.data = {
            ...this.data,
            ...parsedData
          }
          console.log('Data loaded from cache')
          return true
        } else {
          console.log('Cache expired')
          this.clearCache()
        }
      }
    } catch (error) {
      console.error('Failed to load from cache:', error)
      this.clearCache()
    }
    return false
  }

  // 保存数据到本地存储
  saveToCache() {
    try {
      const dataToCache = {
        siteStats: this.data.siteStats,
        recentVisits: this.data.recentVisits,
        pageStats: this.data.pageStats,
        trendData: this.data.trendData,
        durationStats: this.data.durationStats,
        knownVisitors: this.data.knownVisitors,
        todayStats: this.data.todayStats
      }
      
      localStorage.setItem('ournote_data_cache', JSON.stringify(dataToCache))
      localStorage.setItem('ournote_data_cache_meta', JSON.stringify({
        timestamp: Date.now()
      }))
      console.log('Data saved to cache')
    } catch (error) {
      console.error('Failed to save to cache:', error)
    }
  }

  // 清除缓存
  clearCache() {
    try {
      localStorage.removeItem('ournote_data_cache')
      localStorage.removeItem('ournote_data_cache_meta')
      console.log('Cache cleared')
    } catch (error) {
      console.error('Failed to clear cache:', error)
    }
  }

  // 初始化数据管理
  async init() {
    if (this.isInitialized || this.isInitializing) return

    this.isInitializing = true
    try {
      // 首先尝试从缓存加载数据
      const loadedFromCache = this.loadFromCache()
      
      // 无论是否从缓存加载，都设置实时监听
      this.setupRealTimeListeners()
      
      // 如果没有从缓存加载，从Firebase加载初始数据
      if (!loadedFromCache) {
        await this.loadInitialData()
      }
      
      this.isInitialized = true
      console.log('DataManager initialized successfully')
    } catch (error) {
      console.error('Failed to initialize DataManager:', error)
    } finally {
      this.isInitializing = false
    }
  }

  // 确保数据已经初始化
  async ensureInitialized() {
    if (!this.isInitialized) {
      await this.init()
    }
    return this.isInitialized
  }

  // 加载初始数据
  async loadInitialData() {
    try {
      // 并行加载所有数据
      const [siteStatsSnapshot, recentVisitsSnapshot, pageStatsSnapshot, trendDataSnapshot, durationStatsSnapshot, knownVisitorsSnapshot, todayStatsSnapshot] = await Promise.all([
        get(ref(db, 'siteStats')),
        get(ref(db, 'recentVisits')),
        get(ref(db, 'pageStats')),
        get(ref(db, 'trendData')),
        get(ref(db, 'durationStats')),
        get(ref(db, 'knownVisitors')),
        get(ref(db, 'todayStats'))
      ])

      // 更新数据
      if (siteStatsSnapshot.exists()) {
        // 排除 todayViews，优先使用 todayStats 中的数据
        const { todayViews, ...siteStatsWithoutTodayViews } = siteStatsSnapshot.val()
        this.data.siteStats = {
          ...this.data.siteStats,
          ...siteStatsWithoutTodayViews
        }
      }

      if (recentVisitsSnapshot.exists()) {
        const data = recentVisitsSnapshot.val()
        // 新格式：{0: visit, 1: visit, ...}，编号 0 是最新
        if (typeof data === 'object' && !Array.isArray(data)) {
          this.data.recentVisits = Object.entries(data)
            .map(([index, visit]) => ({
              ...visit,
              _index: parseInt(index)
            }))
            .sort((a, b) => a._index - b._index)
            .slice(0, 30)
        } else if (Array.isArray(data)) {
          // 兼容旧格式（数组）
          this.data.recentVisits = data.slice(0, 30)
        }
      }

      if (pageStatsSnapshot.exists()) {
        this.data.pageStats = pageStatsSnapshot.val()
      }

      if (trendDataSnapshot.exists()) {
        this.data.trendData = trendDataSnapshot.val()
      }

      if (durationStatsSnapshot.exists()) {
        this.data.durationStats = durationStatsSnapshot.val()
      }

      if (knownVisitorsSnapshot.exists()) {
        this.data.knownVisitors = knownVisitorsSnapshot.val()
      }

      if (todayStatsSnapshot.exists()) {
        this.data.todayStats = todayStatsSnapshot.val()
      }

      console.log('Initial data loaded successfully')
      this.notifyListeners()
    } catch (error) {
      console.error('Failed to load initial data:', error)
    }
  }

  // 设置实时监听
  setupRealTimeListeners() {
    try {
      // 监听站点统计数据变化
      onValue(ref(db, 'siteStats'), (snapshot) => {
        if (snapshot.exists()) {
          // 排除 todayViews，避免与 todayStats 的数据冲突
          const { todayViews, ...siteStatsWithoutTodayViews } = snapshot.val()
          const newData = {
            ...this.data.siteStats,
            ...siteStatsWithoutTodayViews
          }
          // 只有当数据真正变化时才更新
          if (this.isDataChanged(this.data.siteStats, newData)) {
            this.data.siteStats = newData
            console.log('siteStats updated:', this.data.siteStats)
            // 保存到缓存
            this.saveToCache()
            this.notifyListeners()
          }
        }
      })

      // 监听最近访问记录变化（支持新的对象格式）
      onValue(ref(db, 'recentVisits'), (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val()
          let newData
          // 新格式：{0: visit, 1: visit, ...}，编号 0 是最新
          if (typeof data === 'object' && !Array.isArray(data)) {
            newData = Object.entries(data)
              .map(([index, visit]) => ({
                ...visit,
                _index: parseInt(index)
              }))
              .sort((a, b) => a._index - b._index)
              .slice(0, 30)
          } else if (Array.isArray(data)) {
            // 兼容旧格式（数组）
            newData = data.slice(0, 30)
          }
          // 只有当数据真正变化时才更新
          if (this.isDataChanged(this.data.recentVisits, newData)) {
            this.data.recentVisits = newData
            console.log('recentVisits updated:', this.data.recentVisits)
            // 保存到缓存
            this.saveToCache()
            this.notifyListeners()
          }
        }
      })

      // 监听页面访问统计数据变化
      onValue(ref(db, 'pageStats'), (snapshot) => {
        if (snapshot.exists()) {
          const newData = snapshot.val()
          // 只有当数据真正变化时才更新
          if (this.isDataChanged(this.data.pageStats, newData)) {
            this.data.pageStats = newData
            console.log('pageStats updated:', this.data.pageStats)
            // 保存到缓存
            this.saveToCache()
            this.notifyListeners()
          }
        }
      })

      // 监听访问趋势数据变化
      onValue(ref(db, 'trendData'), (snapshot) => {
        if (snapshot.exists()) {
          const newData = snapshot.val()
          // 只有当数据真正变化时才更新
          if (this.isDataChanged(this.data.trendData, newData)) {
            this.data.trendData = newData
            console.log('trendData updated:', this.data.trendData)
            // 保存到缓存
            this.saveToCache()
            this.notifyListeners()
          }
        }
      })

      // 监听停留时长统计数据变化
      onValue(ref(db, 'durationStats'), (snapshot) => {
        if (snapshot.exists()) {
          const newData = snapshot.val()
          // 只有当数据真正变化时才更新
          if (this.isDataChanged(this.data.durationStats, newData)) {
            this.data.durationStats = newData
            console.log('durationStats updated:', this.data.durationStats)
            // 保存到缓存
            this.saveToCache()
            this.notifyListeners()
          }
        }
      })

      // 监听已知访客数据变化
      onValue(ref(db, 'knownVisitors'), (snapshot) => {
        if (snapshot.exists()) {
          const newData = snapshot.val()
          // 只有当数据真正变化时才更新
          if (this.isDataChanged(this.data.knownVisitors, newData)) {
            this.data.knownVisitors = newData
            console.log('knownVisitors updated:', this.data.knownVisitors)
            // 保存到缓存
            this.saveToCache()
            this.notifyListeners()
          }
        }
      })

      // 监听今日统计数据变化
      onValue(ref(db, 'todayStats'), (snapshot) => {
        if (snapshot.exists()) {
          const newData = snapshot.val()
          // 只有当数据真正变化时才更新
          if (this.isDataChanged(this.data.todayStats, newData)) {
            this.data.todayStats = newData
            console.log('todayStats updated:', this.data.todayStats)
            // 保存到缓存
            this.saveToCache()
            this.notifyListeners()
          }
        }
      })

      console.log('Real-time listeners setup successfully')
    } catch (error) {
      console.error('Failed to setup real-time listeners:', error)
    }
  }

  // 获取数据
  getData(key = null) {
    if (key) {
      return this.data[key] || null
    }
    return this.data
  }

  // 更新数据
  async updateData(key, value) {
    try {
      // 先更新本地数据
      this.data[key] = value
      // 然后同步到Firebase (使用 update 而不是 set 以防覆盖整个节点)
      const updates = {}
      updates[key] = value
      await update(ref(db), updates)
      console.log(`Data ${key} updated successfully`)
      // 保存到缓存
      this.saveToCache()
      this.notifyListeners()
      return true
    } catch (error) {
      console.error(`Failed to update data ${key}:`, error)
      return false
    }
  }

  // 批量更新数据
  async batchUpdateData(updates) {
    try {
      // 先更新本地数据并准备Firebase更新
      Object.keys(updates).forEach(key => {
        this.data[key] = updates[key]
      })

      // 执行Firebase批量更新 (使用 update 而不是 set 以防覆盖整个数据库)
      await update(ref(db), updates)
      console.log('Batch data updated successfully')
      // 保存到缓存
      this.saveToCache()
      this.notifyListeners()
      return true
    } catch (error) {
      console.error('Failed to batch update data:', error)
      return false
    }
  }

  // 添加监听器
  addListener(callback) {
    this.listeners.push(callback)
    // 立即调用一次，提供当前数据
    callback(this.data)
    
    // 返回取消监听的函数
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback)
    }
  }

  // 防抖函数
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // 比较数据是否变化
  isDataChanged(oldData, newData) {
    if (typeof oldData !== typeof newData) return true;
    if (typeof oldData !== 'object' || oldData === null) return oldData !== newData;
    if (Array.isArray(oldData) && Array.isArray(newData)) {
      if (oldData.length !== newData.length) return true;
      for (let i = 0; i < oldData.length; i++) {
        if (this.isDataChanged(oldData[i], newData[i])) return true;
      }
      return false;
    }
    const oldKeys = Object.keys(oldData);
    const newKeys = Object.keys(newData);
    if (oldKeys.length !== newKeys.length) return true;
    for (const key of oldKeys) {
      if (!newKeys.includes(key)) return true;
      if (this.isDataChanged(oldData[key], newData[key])) return true;
    }
    return false;
  }

  // 通知所有监听器（带防抖）
  notifyListeners() {
    // 防抖处理，避免频繁更新
    if (!this._notifyDebounce) {
      // 保存this引用
      const self = this;
      this._notifyDebounce = this.debounce(function() {
        self.listeners.forEach(callback => {
          try {
            callback(self.data)
          } catch (error) {
            console.error('Error in listener callback:', error)
          }
        })

        // 使用BroadcastChannel通知其他标签页
        try {
          const broadcastChannel = new BroadcastChannel('ournote-data');
          broadcastChannel.postMessage({ type: 'dataUpdate', data: self.data });
        } catch (e) {
          // 如果浏览器不支持BroadcastChannel，忽略
        }
      }, 100); // 100ms防抖
    }
    this._notifyDebounce();
  }

  // 初始化BroadcastChannel监听
  initBroadcastChannel() {
    try {
      const broadcastChannel = new BroadcastChannel('ournote-data');
      broadcastChannel.onmessage = (event) => {
        if (event.data.type === 'dataUpdate') {
          // 更新本地数据
          this.data = {
            ...this.data,
            ...event.data.data
          }
          console.log('Data updated from other tab')
          this.notifyListeners()
        }
      };
    } catch (e) {
      // 如果浏览器不支持BroadcastChannel，忽略
    }
  }
}

// 初始化BroadcastChannel监听
const dataManager = DataManager.getInstance()
dataManager.initBroadcastChannel()

export default dataManager